/**
 * Brazilian Demo Data
 * Mock data for Brazilian market demo
 * @task B.INV.BR - Brazilian investor demo data
 *
 * This module provides Brazilian-specific mock data including:
 * - Vendors with CNPJ, Brazilian addresses, PIX keys
 * - Customers with CNPJ, credit limits in BRL
 * - Bills (Contas a Pagar) with Brazilian tax breakdown
 * - Invoices (NF-e) with ICMS, PIS, COFINS
 */

import type { BillStatus, InvoiceStatus } from './demo-data';

// Brazilian-specific types (standalone, not extending base types)
export interface VendorBR {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  contactName: string;
  openBalance: number;
  totalPurchases: number;
  status: 'active' | 'inactive';
  paymentTerms: string;
  createdAt: string;
  updatedAt: string;
  // Brazilian-specific fields
  cnpj: string;
  tradeName?: string;
  stateRegistration?: string;
  municipalRegistration?: string;
  pixKey?: string;
  bankAccount?: {
    bank: string;
    agency: string;
    account: string;
  };
  taxId?: string; // Alias for cnpj compatibility
}

export interface CustomerBR {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  contactName: string;
  openBalance: number;
  totalRevenue: number;
  creditLimit: number;
  status: 'active' | 'inactive' | 'on-hold';
  paymentTerms: string;
  createdAt: string;
  updatedAt: string;
  // Brazilian-specific fields
  cnpj: string;
  tradeName?: string;
  stateRegistration?: string;
  taxId?: string; // Alias for cnpj compatibility
}

export interface BillBR {
  id: string;
  billNumber: string;
  vendorId: string;
  vendorName: string;
  amount: number;
  dueDate: string;
  issueDate: string;
  status: BillStatus;
  category: string;
  description: string;
  glAccountId: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Brazilian-specific fields
  cnpj: string;
  nfeNumber?: string;
  nfeAccessKey?: string;
  taxes?: {
    icms?: { base: number; rate: number; amount: number };
    pis?: { base: number; rate: number; amount: number };
    cofins?: { base: number; rate: number; amount: number };
    iss?: { base: number; rate: number; amount: number };
  };
}

export interface InvoiceBR {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  amount: number;
  paidAmount: number;
  issueDate: string;
  dueDate: string;
  status: InvoiceStatus;
  description: string;
  lineItems: Array<{ description: string; quantity: number; unitPrice: number; amount: number }>;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Brazilian-specific fields
  cnpj: string;
  nfe: {
    number: string;
    series: string;
    accessKey: string;
    status: 'autorizada' | 'cancelada' | 'denegada' | 'pendente';
    protocol?: string;
    authDate?: string;
  };
  taxes: {
    icms?: { base: number; rate: number; amount: number };
    pis: { base: number; rate: number; amount: number };
    cofins: { base: number; rate: number; amount: number };
    iss?: { base: number; rate: number; amount: number };
    total: number;
  };
}

// ============================================================================
// Brazilian Vendors (Fornecedores)
// ============================================================================

export const demoVendorsBR: VendorBR[] = [
  {
    id: 'vbr-001',
    name: 'ABC Distribuidora de Alimentos LTDA',
    tradeName: 'ABC Alimentos',
    cnpj: '12.345.678/0001-99',
    email: 'financeiro@abcdistribuidora.com.br',
    phone: '+55 (11) 3456-7890',
    address: 'Av. Paulista, 1000',
    city: 'São Paulo',
    state: 'SP',
    postalCode: '01310-100',
    country: 'Brasil',
    contactName: 'Maria Silva',
    openBalance: 15432.50,
    totalPurchases: 245000.00,
    status: 'active',
    paymentTerms: '30 dias',
    createdAt: '2024-01-15',
    updatedAt: '2026-01-10',
    pixKey: '12345678000199',
    bankAccount: { bank: 'Banco do Brasil', agency: '1234-5', account: '12345-6' },
  },
  {
    id: 'vbr-002',
    name: 'Tech Solutions Brasil LTDA',
    tradeName: 'Tech Solutions',
    cnpj: '98.765.432/0001-10',
    email: 'contas@techsolutions.com.br',
    phone: '+55 (21) 2222-3333',
    address: 'Rua da Assembleia, 100',
    city: 'Rio de Janeiro',
    state: 'RJ',
    postalCode: '20011-000',
    country: 'Brasil',
    contactName: 'Carlos Oliveira',
    openBalance: 8750.00,
    totalPurchases: 125000.00,
    status: 'active',
    paymentTerms: '45 dias',
    createdAt: '2024-03-20',
    updatedAt: '2026-01-08',
    pixKey: 'contas@techsolutions.com.br',
    bankAccount: { bank: 'Itaú', agency: '5678-9', account: '67890-1' },
  },
  {
    id: 'vbr-003',
    name: 'Logística Sul Transportes LTDA',
    tradeName: 'Logística Sul',
    cnpj: '11.222.333/0001-44',
    email: 'faturamento@logisticasul.com.br',
    phone: '+55 (51) 3344-5566',
    address: 'Av. Ipiranga, 500',
    city: 'Porto Alegre',
    state: 'RS',
    postalCode: '90035-000',
    country: 'Brasil',
    contactName: 'Ana Souza',
    openBalance: 23500.00,
    totalPurchases: 180000.00,
    status: 'active',
    paymentTerms: '30 dias',
    createdAt: '2024-05-10',
    updatedAt: '2026-01-12',
    pixKey: '+5551997773333',
    bankAccount: { bank: 'Banrisul', agency: '1122-3', account: '33445-5' },
  },
  {
    id: 'vbr-004',
    name: 'Escritório Contábil Central LTDA',
    tradeName: 'Contabilidade Central',
    cnpj: '44.555.666/0001-77',
    email: 'contato@contabilidadecentral.com.br',
    phone: '+55 (31) 3344-7788',
    address: 'Rua Espírito Santo, 200',
    city: 'Belo Horizonte',
    state: 'MG',
    postalCode: '30130-100',
    country: 'Brasil',
    contactName: 'Roberto Almeida',
    openBalance: 4500.00,
    totalPurchases: 54000.00,
    status: 'active',
    paymentTerms: '15 dias',
    createdAt: '2024-02-01',
    updatedAt: '2026-01-05',
    pixKey: '44555666000177',
    bankAccount: { bank: 'Bradesco', agency: '3344-5', account: '55667-7' },
  },
  {
    id: 'vbr-005',
    name: 'Marketing Digital Pro LTDA',
    tradeName: 'MD Pro',
    cnpj: '55.666.777/0001-88',
    email: 'financeiro@mdpro.com.br',
    phone: '+55 (11) 4444-5555',
    address: 'Av. Brigadeiro Faria Lima, 1500',
    city: 'São Paulo',
    state: 'SP',
    postalCode: '01452-001',
    country: 'Brasil',
    contactName: 'Fernanda Lima',
    openBalance: 12000.00,
    totalPurchases: 96000.00,
    status: 'active',
    paymentTerms: '30 dias',
    createdAt: '2024-04-15',
    updatedAt: '2026-01-15',
    pixKey: 'financeiro@mdpro.com.br',
    bankAccount: { bank: 'Nubank', agency: '0001', account: '123456-7' },
  },
  {
    id: 'vbr-006',
    name: 'Cloud Services Brasil LTDA',
    tradeName: 'CloudBR',
    cnpj: '77.888.999/0001-00',
    email: 'billing@cloudbr.com.br',
    phone: '+55 (11) 5555-6666',
    address: 'Rua Funchal, 400',
    city: 'São Paulo',
    state: 'SP',
    postalCode: '04551-060',
    country: 'Brasil',
    contactName: 'Lucas Santos',
    openBalance: 2999.99,
    totalPurchases: 35999.88,
    status: 'active',
    paymentTerms: 'Mensal (dia 10)',
    createdAt: '2024-06-01',
    updatedAt: '2026-01-10',
    pixKey: 'billing@cloudbr.com.br',
    bankAccount: { bank: 'Inter', agency: '0001', account: '987654-3' },
  },
  {
    id: 'vbr-007',
    name: 'Advocacia Silva & Associados',
    tradeName: 'Silva Advogados',
    cnpj: '88.999.000/0001-11',
    email: 'financeiro@silvaadvogados.com.br',
    phone: '+55 (11) 3111-2222',
    address: 'Av. Angélica, 2500',
    city: 'São Paulo',
    state: 'SP',
    postalCode: '01228-200',
    country: 'Brasil',
    contactName: 'Dr. Pedro Silva',
    openBalance: 18500.00,
    totalPurchases: 74000.00,
    status: 'active',
    paymentTerms: 'À vista',
    createdAt: '2024-07-20',
    updatedAt: '2026-01-18',
    pixKey: '88999000000111',
    bankAccount: { bank: 'Santander', agency: '6677-8', account: '99001-1' },
  },
  {
    id: 'vbr-008',
    name: 'Papelaria e Suprimentos Express LTDA',
    tradeName: 'Papelaria Express',
    cnpj: '99.000.111/0001-22',
    email: 'pedidos@papelariaexpress.com.br',
    phone: '+55 (11) 2222-3344',
    address: 'Rua Augusta, 1200',
    city: 'São Paulo',
    state: 'SP',
    postalCode: '01305-100',
    country: 'Brasil',
    contactName: 'Juliana Ferreira',
    openBalance: 1250.00,
    totalPurchases: 15000.00,
    status: 'active',
    paymentTerms: '15 dias',
    createdAt: '2024-08-10',
    updatedAt: '2026-01-02',
    pixKey: '+5511955556677',
    bankAccount: { bank: 'Banco do Brasil', agency: '7788-9', account: '11223-3' },
  },
];

// ============================================================================
// Brazilian Customers (Clientes)
// ============================================================================

export const demoCustomersBR: CustomerBR[] = [
  {
    id: 'cbr-001',
    name: 'Grupo Empresarial Sul LTDA',
    tradeName: 'Grupo Sul',
    cnpj: '11.222.333/0001-44',
    email: 'compras@gruposul.com.br',
    phone: '+55 (51) 3344-5566',
    address: 'Av. Ipiranga, 500',
    city: 'Porto Alegre',
    state: 'RS',
    postalCode: '90035-000',
    country: 'Brasil',
    contactName: 'Ricardo Mendes',
    openBalance: 45000.00,
    totalRevenue: 890000.00,
    creditLimit: 100000.00,
    status: 'active',
    paymentTerms: '30 dias',
    createdAt: '2024-01-10',
    updatedAt: '2026-01-15',
  },
  {
    id: 'cbr-002',
    name: 'Indústria Metalúrgica Nacional S/A',
    tradeName: 'MetalNac',
    cnpj: '22.333.444/0001-55',
    email: 'financeiro@metalnac.com.br',
    phone: '+55 (19) 3233-4455',
    address: 'Rod. Anhanguera, km 115',
    city: 'Campinas',
    state: 'SP',
    postalCode: '13054-750',
    country: 'Brasil',
    contactName: 'Helena Costa',
    openBalance: 125000.00,
    totalRevenue: 1250000.00,
    creditLimit: 200000.00,
    status: 'active',
    paymentTerms: '45 dias',
    createdAt: '2024-02-20',
    updatedAt: '2026-01-14',
  },
  {
    id: 'cbr-003',
    name: 'Clínica Médica Saúde Total LTDA',
    tradeName: 'Saúde Total',
    cnpj: '33.444.555/0001-66',
    email: 'adm@saudetotal.med.br',
    phone: '+55 (11) 4455-6677',
    address: 'Rua Oscar Freire, 800',
    city: 'São Paulo',
    state: 'SP',
    postalCode: '01426-001',
    country: 'Brasil',
    contactName: 'Dr. André Martins',
    openBalance: 18500.00,
    totalRevenue: 222000.00,
    creditLimit: 50000.00,
    status: 'active',
    paymentTerms: '15 dias',
    createdAt: '2024-03-15',
    updatedAt: '2026-01-13',
  },
  {
    id: 'cbr-004',
    name: 'Construtora Horizonte LTDA',
    tradeName: 'Horizonte Construções',
    cnpj: '44.555.666/0001-77',
    email: 'projetos@horizonteconstrucoes.com.br',
    phone: '+55 (21) 3456-7890',
    address: 'Av. Rio Branco, 150',
    city: 'Rio de Janeiro',
    state: 'RJ',
    postalCode: '20040-003',
    country: 'Brasil',
    contactName: 'Eng. Marcelo Ribeiro',
    openBalance: 85000.00,
    totalRevenue: 680000.00,
    creditLimit: 150000.00,
    status: 'active',
    paymentTerms: '60 dias',
    createdAt: '2024-04-10',
    updatedAt: '2026-01-10',
  },
  {
    id: 'cbr-005',
    name: 'Startup Inovação Digital LTDA',
    tradeName: 'InovaTech',
    cnpj: '66.777.888/0001-99',
    email: 'finance@inovatech.io',
    phone: '+55 (11) 5678-9012',
    address: 'Rua Funchal, 500',
    city: 'São Paulo',
    state: 'SP',
    postalCode: '04551-060',
    country: 'Brasil',
    contactName: 'Bruno Gomes',
    openBalance: 12500.00,
    totalRevenue: 150000.00,
    creditLimit: 30000.00,
    status: 'active',
    paymentTerms: '15 dias',
    createdAt: '2024-05-20',
    updatedAt: '2026-01-08',
  },
  {
    id: 'cbr-006',
    name: 'Fazenda Bom Jardim Agropecuária LTDA',
    tradeName: 'Bom Jardim Agro',
    cnpj: '99.000.111/0001-22',
    email: 'comercial@bomjardim.agr.br',
    phone: '+55 (62) 3456-7890',
    address: 'Rod. GO-060, km 45',
    city: 'Goiânia',
    state: 'GO',
    postalCode: '74000-000',
    country: 'Brasil',
    contactName: 'José Ferreira',
    openBalance: 65000.00,
    totalRevenue: 520000.00,
    creditLimit: 100000.00,
    status: 'active',
    paymentTerms: '45 dias',
    createdAt: '2024-06-15',
    updatedAt: '2026-01-05',
  },
  {
    id: 'cbr-007',
    name: 'E-commerce Brasil Digital LTDA',
    tradeName: 'Brasil Digital',
    cnpj: '00.111.222/0001-33',
    email: 'parceiros@brasildigital.com.br',
    phone: '+55 (11) 6789-0123',
    address: 'Av. Eng. Luís Carlos Berrini, 1000',
    city: 'São Paulo',
    state: 'SP',
    postalCode: '04571-000',
    country: 'Brasil',
    contactName: 'Felipe Rodrigues',
    openBalance: 32000.00,
    totalRevenue: 384000.00,
    creditLimit: 60000.00,
    status: 'active',
    paymentTerms: '30 dias',
    createdAt: '2024-07-01',
    updatedAt: '2026-01-03',
  },
];

// ============================================================================
// Brazilian Bills (Contas a Pagar)
// ============================================================================

export const demoBillsBR: BillBR[] = [
  {
    id: 'bbr-001',
    billNumber: 'CP-2026-0001',
    vendorId: 'vbr-001',
    vendorName: 'ABC Distribuidora de Alimentos LTDA',
    cnpj: '12.345.678/0001-99',
    amount: 15432.50,
    dueDate: '2026-02-14',
    issueDate: '2026-01-15',
    status: 'pending' as BillStatus,
    category: 'Mercadorias',
    description: 'Compra de alimentos para revenda - Janeiro/2026',
    glAccountId: 'gl-500',
    notes: 'NF-e 000001234',
    nfeNumber: '000001234',
    nfeAccessKey: '35260112345678000199550010000012341234567890',
    createdAt: '2026-01-15',
    updatedAt: '2026-01-15',
    taxes: {
      icms: { base: 15432.50, rate: 18, amount: 2777.85 },
      pis: { base: 15432.50, rate: 1.65, amount: 254.64 },
      cofins: { base: 15432.50, rate: 7.6, amount: 1172.87 },
    },
  },
  {
    id: 'bbr-002',
    billNumber: 'CP-2026-0002',
    vendorId: 'vbr-002',
    vendorName: 'Tech Solutions Brasil LTDA',
    cnpj: '98.765.432/0001-10',
    amount: 8750.00,
    dueDate: '2026-02-28',
    issueDate: '2026-01-14',
    status: 'pending' as BillStatus,
    category: 'Serviços de TI',
    description: 'Manutenção de sistemas - Janeiro/2026',
    glAccountId: 'gl-510',
    notes: 'Contrato mensal',
    createdAt: '2026-01-14',
    updatedAt: '2026-01-14',
    taxes: {
      iss: { base: 8750.00, rate: 5, amount: 437.50 },
      pis: { base: 8750.00, rate: 0.65, amount: 56.88 },
      cofins: { base: 8750.00, rate: 3, amount: 262.50 },
    },
  },
  {
    id: 'bbr-003',
    billNumber: 'CP-2026-0003',
    vendorId: 'vbr-003',
    vendorName: 'Logística Sul Transportes LTDA',
    cnpj: '11.222.333/0001-44',
    amount: 23500.00,
    dueDate: '2026-02-10',
    issueDate: '2026-01-10',
    status: 'approved' as BillStatus,
    category: 'Frete e Logística',
    description: 'Serviço de transporte - Janeiro/2026',
    glAccountId: 'gl-520',
    notes: 'CT-e 000005678',
    createdAt: '2026-01-10',
    updatedAt: '2026-01-12',
    taxes: {
      icms: { base: 23500.00, rate: 12, amount: 2820.00 },
      pis: { base: 23500.00, rate: 1.65, amount: 387.75 },
      cofins: { base: 23500.00, rate: 7.6, amount: 1786.00 },
    },
  },
  {
    id: 'bbr-004',
    billNumber: 'CP-2026-0004',
    vendorId: 'vbr-004',
    vendorName: 'Escritório Contábil Central LTDA',
    cnpj: '44.555.666/0001-77',
    amount: 4500.00,
    dueDate: '2026-01-20',
    issueDate: '2026-01-05',
    status: 'paid' as BillStatus,
    category: 'Serviços Contábeis',
    description: 'Honorários contábeis - Janeiro/2026',
    glAccountId: 'gl-530',
    notes: 'Pagamento via PIX',
    createdAt: '2026-01-05',
    updatedAt: '2026-01-20',
    taxes: {
      iss: { base: 4500.00, rate: 5, amount: 225.00 },
      pis: { base: 4500.00, rate: 0.65, amount: 29.25 },
      cofins: { base: 4500.00, rate: 3, amount: 135.00 },
    },
  },
  {
    id: 'bbr-005',
    billNumber: 'CP-2026-0005',
    vendorId: 'vbr-005',
    vendorName: 'Marketing Digital Pro LTDA',
    cnpj: '55.666.777/0001-88',
    amount: 12000.00,
    dueDate: '2026-02-15',
    issueDate: '2026-01-15',
    status: 'pending' as BillStatus,
    category: 'Marketing',
    description: 'Campanha digital - Janeiro/2026',
    glAccountId: 'gl-540',
    notes: 'Google Ads + Meta Ads',
    createdAt: '2026-01-15',
    updatedAt: '2026-01-15',
    taxes: {
      iss: { base: 12000.00, rate: 5, amount: 600.00 },
      pis: { base: 12000.00, rate: 0.65, amount: 78.00 },
      cofins: { base: 12000.00, rate: 3, amount: 360.00 },
    },
  },
  {
    id: 'bbr-006',
    billNumber: 'CP-2026-0006',
    vendorId: 'vbr-006',
    vendorName: 'Cloud Services Brasil LTDA',
    cnpj: '77.888.999/0001-00',
    amount: 2999.99,
    dueDate: '2026-02-10',
    issueDate: '2026-01-10',
    status: 'pending' as BillStatus,
    category: 'Infraestrutura',
    description: 'Serviços de nuvem - Janeiro/2026',
    glAccountId: 'gl-550',
    notes: 'AWS + GCP',
    createdAt: '2026-01-10',
    updatedAt: '2026-01-10',
    taxes: {
      iss: { base: 2999.99, rate: 5, amount: 150.00 },
      pis: { base: 2999.99, rate: 0.65, amount: 19.50 },
      cofins: { base: 2999.99, rate: 3, amount: 90.00 },
    },
  },
  {
    id: 'bbr-007',
    billNumber: 'CP-2026-0007',
    vendorId: 'vbr-007',
    vendorName: 'Advocacia Silva & Associados',
    cnpj: '88.999.000/0001-11',
    amount: 18500.00,
    dueDate: '2026-01-18',
    issueDate: '2026-01-08',
    status: 'overdue' as BillStatus,
    category: 'Serviços Jurídicos',
    description: 'Consultoria jurídica - Janeiro/2026',
    glAccountId: 'gl-560',
    notes: 'Honorários advocatícios',
    createdAt: '2026-01-08',
    updatedAt: '2026-01-18',
    taxes: {
      iss: { base: 18500.00, rate: 5, amount: 925.00 },
      pis: { base: 18500.00, rate: 0.65, amount: 120.25 },
      cofins: { base: 18500.00, rate: 3, amount: 555.00 },
    },
  },
  {
    id: 'bbr-008',
    billNumber: 'CP-2026-0008',
    vendorId: 'vbr-008',
    vendorName: 'Papelaria e Suprimentos Express LTDA',
    cnpj: '99.000.111/0001-22',
    amount: 1250.00,
    dueDate: '2026-01-25',
    issueDate: '2026-01-10',
    status: 'paid' as BillStatus,
    category: 'Material de Escritório',
    description: 'Suprimentos de escritório - Janeiro/2026',
    glAccountId: 'gl-570',
    notes: 'Pagamento antecipado',
    createdAt: '2026-01-10',
    updatedAt: '2026-01-12',
    taxes: {
      icms: { base: 1250.00, rate: 18, amount: 225.00 },
      pis: { base: 1250.00, rate: 1.65, amount: 20.63 },
      cofins: { base: 1250.00, rate: 7.6, amount: 95.00 },
    },
  },
];

// ============================================================================
// Brazilian Invoices (Notas Fiscais - NF-e)
// ============================================================================

export const demoInvoicesBR: InvoiceBR[] = [
  {
    id: 'ibr-001',
    invoiceNumber: 'NF-e 000001234',
    customerId: 'cbr-001',
    customerName: 'Grupo Empresarial Sul LTDA',
    cnpj: '11.222.333/0001-44',
    amount: 15000.00,
    paidAmount: 0,
    issueDate: '2026-01-15',
    dueDate: '2026-02-14',
    status: 'sent' as InvoiceStatus,
    description: 'Consultoria Financeira - Janeiro 2026',
    lineItems: [
      { description: 'Consultoria Financeira', quantity: 1, unitPrice: 15000.00, amount: 15000.00 },
    ],
    notes: 'Fatura mensal',
    createdAt: '2026-01-15',
    updatedAt: '2026-01-15',
    nfe: {
      number: '000001234',
      series: '1',
      accessKey: '35260112345678000199550010000012341234567890',
      status: 'autorizada',
      protocol: '135260000012345',
      authDate: '2026-01-15T10:30:00',
    },
    taxes: {
      icms: { base: 15000.00, rate: 18, amount: 2700.00 },
      pis: { base: 15000.00, rate: 1.65, amount: 247.50 },
      cofins: { base: 15000.00, rate: 7.6, amount: 1140.00 },
      total: 4087.50,
    },
  },
  {
    id: 'ibr-002',
    invoiceNumber: 'NF-e 000001235',
    customerId: 'cbr-002',
    customerName: 'Indústria Metalúrgica Nacional S/A',
    cnpj: '22.333.444/0001-55',
    amount: 85000.00,
    paidAmount: 0,
    issueDate: '2026-01-14',
    dueDate: '2026-02-28',
    status: 'sent' as InvoiceStatus,
    description: 'Implementação Sistema ERP - Fase 1',
    lineItems: [
      { description: 'Implementação Sistema ERP - Fase 1', quantity: 1, unitPrice: 75000.00, amount: 75000.00 },
      { description: 'Treinamento Equipe (40h)', quantity: 40, unitPrice: 250.00, amount: 10000.00 },
    ],
    notes: 'Contrato #2026-001',
    createdAt: '2026-01-14',
    updatedAt: '2026-01-14',
    nfe: {
      number: '000001235',
      series: '1',
      accessKey: '35260112345678000199550010000012351234567891',
      status: 'autorizada',
      protocol: '135260000012346',
      authDate: '2026-01-14T14:22:00',
    },
    taxes: {
      iss: { base: 85000.00, rate: 5, amount: 4250.00 },
      pis: { base: 85000.00, rate: 0.65, amount: 552.50 },
      cofins: { base: 85000.00, rate: 3, amount: 2550.00 },
      total: 7352.50,
    },
  },
  {
    id: 'ibr-003',
    invoiceNumber: 'NF-e 000001236',
    customerId: 'cbr-003',
    customerName: 'Clínica Médica Saúde Total LTDA',
    cnpj: '33.444.555/0001-66',
    amount: 2000.00,
    paidAmount: 2000.00,
    issueDate: '2026-01-13',
    dueDate: '2026-01-28',
    status: 'paid' as InvoiceStatus,
    description: 'Licença Software Financeiro - Mensal',
    lineItems: [
      { description: 'Licença Software Financeiro - Mensal', quantity: 1, unitPrice: 1500.00, amount: 1500.00 },
      { description: 'Suporte Técnico Premium', quantity: 1, unitPrice: 500.00, amount: 500.00 },
    ],
    notes: 'Pago via PIX',
    createdAt: '2026-01-13',
    updatedAt: '2026-01-15',
    nfe: {
      number: '000001236',
      series: '1',
      accessKey: '35260112345678000199550010000012361234567892',
      status: 'autorizada',
      protocol: '135260000012347',
      authDate: '2026-01-13T09:15:00',
    },
    taxes: {
      iss: { base: 2000.00, rate: 5, amount: 100.00 },
      pis: { base: 2000.00, rate: 0.65, amount: 13.00 },
      cofins: { base: 2000.00, rate: 3, amount: 60.00 },
      total: 173.00,
    },
  },
  {
    id: 'ibr-004',
    invoiceNumber: 'NF-e 000001237',
    customerId: 'cbr-004',
    customerName: 'Construtora Horizonte LTDA',
    cnpj: '44.555.666/0001-77',
    amount: 28000.00,
    paidAmount: 0,
    issueDate: '2026-01-10',
    dueDate: '2026-03-11',
    status: 'sent' as InvoiceStatus,
    description: 'Consultoria Fiscal - Projeto Especial',
    lineItems: [
      { description: 'Consultoria Fiscal - Projeto Especial', quantity: 80, unitPrice: 350.00, amount: 28000.00 },
    ],
    notes: 'Projeto expansão RJ',
    createdAt: '2026-01-10',
    updatedAt: '2026-01-10',
    nfe: {
      number: '000001237',
      series: '1',
      accessKey: '35260112345678000199550010000012371234567893',
      status: 'autorizada',
      protocol: '135260000012348',
      authDate: '2026-01-10T16:45:00',
    },
    taxes: {
      iss: { base: 28000.00, rate: 5, amount: 1400.00 },
      pis: { base: 28000.00, rate: 0.65, amount: 182.00 },
      cofins: { base: 28000.00, rate: 3, amount: 840.00 },
      total: 2422.00,
    },
  },
  {
    id: 'ibr-005',
    invoiceNumber: 'NF-e 000001238',
    customerId: 'cbr-005',
    customerName: 'Startup Inovação Digital LTDA',
    cnpj: '66.777.888/0001-99',
    amount: 7500.00,
    paidAmount: 7500.00,
    issueDate: '2026-01-08',
    dueDate: '2026-01-23',
    status: 'paid' as InvoiceStatus,
    description: 'Setup Inicial Finova - Plano Professional',
    lineItems: [
      { description: 'Setup Inicial Finova - Plano Professional', quantity: 1, unitPrice: 5000.00, amount: 5000.00 },
      { description: 'Migração de Dados', quantity: 1, unitPrice: 2500.00, amount: 2500.00 },
    ],
    notes: 'Pago com cartão de crédito',
    createdAt: '2026-01-08',
    updatedAt: '2026-01-08',
    nfe: {
      number: '000001238',
      series: '1',
      accessKey: '35260112345678000199550010000012381234567894',
      status: 'autorizada',
      protocol: '135260000012349',
      authDate: '2026-01-08T11:30:00',
    },
    taxes: {
      iss: { base: 7500.00, rate: 5, amount: 375.00 },
      pis: { base: 7500.00, rate: 0.65, amount: 48.75 },
      cofins: { base: 7500.00, rate: 3, amount: 225.00 },
      total: 648.75,
    },
  },
  {
    id: 'ibr-006',
    invoiceNumber: 'NF-e 000001239',
    customerId: 'cbr-006',
    customerName: 'Fazenda Bom Jardim Agropecuária LTDA',
    cnpj: '99.000.111/0001-22',
    amount: 57000.00,
    paidAmount: 0,
    issueDate: '2026-01-05',
    dueDate: '2026-02-19',
    status: 'sent' as InvoiceStatus,
    description: 'Licença Anual - Plano Enterprise',
    lineItems: [
      { description: 'Licença Anual - Plano Enterprise', quantity: 1, unitPrice: 45000.00, amount: 45000.00 },
      { description: 'Integração API - Módulo Agro', quantity: 1, unitPrice: 12000.00, amount: 12000.00 },
    ],
    notes: 'Contrato anual renovado',
    createdAt: '2026-01-05',
    updatedAt: '2026-01-05',
    nfe: {
      number: '000001239',
      series: '1',
      accessKey: '35260112345678000199550010000012391234567895',
      status: 'autorizada',
      protocol: '135260000012350',
      authDate: '2026-01-05T08:00:00',
    },
    taxes: {
      iss: { base: 57000.00, rate: 5, amount: 2850.00 },
      pis: { base: 57000.00, rate: 0.65, amount: 370.50 },
      cofins: { base: 57000.00, rate: 3, amount: 1710.00 },
      total: 4930.50,
    },
  },
  {
    id: 'ibr-007',
    invoiceNumber: 'NF-e 000001240',
    customerId: 'cbr-007',
    customerName: 'E-commerce Brasil Digital LTDA',
    cnpj: '00.111.222/0001-33',
    amount: 4000.00,
    paidAmount: 0,
    issueDate: '2026-01-03',
    dueDate: '2026-02-02',
    status: 'overdue' as InvoiceStatus,
    description: 'Licença Mensal - Plano Professional',
    lineItems: [
      { description: 'Licença Mensal - Plano Professional', quantity: 1, unitPrice: 2500.00, amount: 2500.00 },
      { description: 'Integração Marketplace (Mercado Livre)', quantity: 1, unitPrice: 1500.00, amount: 1500.00 },
    ],
    notes: 'Vencida - aguardando contato',
    createdAt: '2026-01-03',
    updatedAt: '2026-01-03',
    nfe: {
      number: '000001240',
      series: '1',
      accessKey: '35260112345678000199550010000012401234567896',
      status: 'autorizada',
      protocol: '135260000012351',
      authDate: '2026-01-03T15:20:00',
    },
    taxes: {
      iss: { base: 4000.00, rate: 5, amount: 200.00 },
      pis: { base: 4000.00, rate: 0.65, amount: 26.00 },
      cofins: { base: 4000.00, rate: 3, amount: 120.00 },
      total: 346.00,
    },
  },
];

// Alias exports for useMarketApi compatibility
export const brazilianVendors = demoVendorsBR;
export const brazilianCustomers = demoCustomersBR;
export const brazilianBills = demoBillsBR;
export const brazilianInvoices = demoInvoicesBR;

// Export summary calculations
export function getBrazilianSummary() {
  const totalAR = demoInvoicesBR
    .filter((inv) => inv.status !== 'paid' && inv.status !== 'void')
    .reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0);

  const totalAP = demoBillsBR
    .filter((bill) => bill.status !== 'paid')
    .reduce((sum, bill) => sum + bill.amount, 0);

  const overdueAR = demoInvoicesBR
    .filter((inv) => inv.status === 'overdue')
    .reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0);

  const overdueAP = demoBillsBR
    .filter((bill) => bill.status === 'overdue')
    .reduce((sum, bill) => sum + bill.amount, 0);

  return {
    totalReceivables: totalAR,
    totalPayables: totalAP,
    overdueReceivables: overdueAR,
    overduePayables: overdueAP,
    totalRevenue: 198500.00, // Sum of all invoices
    totalExpenses: 86932.49, // Sum of all bills
    netIncome: 111567.51, // Revenue - Expenses
    revenueGrowth: 12.5, // Percentage growth
    cashPosition: 185000.00,
  };
}
